import {
  type BulkResult,
  type ColumnTable,
  columnsSettingKey,
  type SavedFilterItem,
  type SavedFilterPage,
  type SortDir,
} from '@erp/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Bookmark,
  ChevronDown,
  Columns3,
  Download,
  SlidersHorizontal,
  Trash2,
  X,
} from 'lucide-react';
import { type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { plural } from '@/lib/format';
import { Dialog } from './dialog';
import { Button, Input } from './ui';

/**
 * Общие инструменты списков (задачи, сделки, клиенты): сортировка кликом по заголовку, панель
 * фильтра, готовые и сохранённые фильтры, выбор строк с групповым действием, выгрузка в Excel.
 */

// ---------- Сортировка ----------

export interface SortState<K extends string> {
  sort?: K;
  dir?: SortDir;
}

/** Заголовок колонки: первый клик — по возрастанию, второй — по убыванию, третий — снять. */
export function SortHeader<K extends string>({
  label,
  sortKey,
  state,
  onSort,
  className,
  align = 'left',
}: {
  label: string;
  sortKey: K;
  state: SortState<K>;
  onSort: (next: SortState<K>) => void;
  className?: string;
  align?: 'left' | 'right';
}) {
  const active = state.sort === sortKey;
  const dir = active ? (state.dir ?? 'asc') : undefined;
  const next = (): SortState<K> =>
    !active ? { sort: sortKey, dir: 'asc' } : dir === 'asc' ? { sort: sortKey, dir: 'desc' } : {};
  const Icon = dir === 'asc' ? ArrowUp : dir === 'desc' ? ArrowDown : ArrowUpDown;
  return (
    <th
      scope="col"
      aria-sort={dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
      className={clsx('px-4 py-2.5 font-medium', align === 'right' && 'text-right', className)}
    >
      <button
        type="button"
        onClick={() => onSort(next())}
        className={clsx(
          'group inline-flex items-center gap-1 whitespace-nowrap hover:text-ink',
          active && 'text-ink',
          align === 'right' && 'flex-row-reverse',
        )}
      >
        {label}
        <Icon
          aria-hidden
          className={clsx('size-3.5', active ? 'opacity-100' : 'opacity-0 group-hover:opacity-60')}
        />
      </button>
    </th>
  );
}

// ---------- Выбор строк ----------

export function useSelection(visibleIds: string[]) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const key = visibleIds.join(',');
  // Строки ушли из списка (другой фильтр) — снимаем с них выделение
  // biome-ignore lint/correctness/useExhaustiveDependencies: пересчёт только при смене набора строк
  useEffect(() => {
    setSelected((prev) => {
      const visible = new Set(visibleIds);
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [key]);
  const all = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  return {
    selected,
    count: selected.size,
    all,
    some: selected.size > 0 && !all,
    has: (id: string) => selected.has(id),
    toggle: (id: string) =>
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      }),
    toggleAll: () => setSelected(all ? new Set() : new Set(visibleIds)),
    clear: () => setSelected(new Set()),
  };
}

export function SelectBox({
  checked,
  indeterminate,
  onChange,
  label,
}: {
  checked: boolean;
  indeterminate?: boolean;
  onChange: () => void;
  label: string;
}) {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate);
  }, [indeterminate]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={checked}
      onChange={onChange}
      aria-label={label}
      className="size-4 cursor-pointer align-middle accent-[var(--color-graphite)]"
    />
  );
}

/** Полоса над списком, пока выбраны строки: сколько выбрано, действия, «снять выделение». */
export function BulkBar({
  count,
  total,
  onSelectAllMatching,
  allMatching,
  onClear,
  children,
}: {
  count: number;
  /** Сколько всего по фильтру — предложить «выбрать все N» */
  total?: number;
  onSelectAllMatching?: () => void;
  allMatching?: boolean;
  onClear: () => void;
  children: ReactNode;
}) {
  return (
    <section
      aria-label="Действия с выбранными"
      className="sticky top-14 z-20 mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[var(--radius-panel)] border border-graphite bg-graphite px-4 py-2.5 text-[13.5px] text-white"
    >
      <span className="font-medium tabular-nums">
        {allMatching ? `Все ${total} по фильтру` : `Выбрано ${count}`}
      </span>
      {!allMatching && onSelectAllMatching && total !== undefined && total > count && (
        <button type="button" onClick={onSelectAllMatching} className="underline underline-offset-2">
          Выбрать все {total}
        </button>
      )}
      <div className="flex min-w-0 flex-wrap items-center gap-2 [&_.input]:h-8 [&_.input]:bg-white [&_.input]:text-ink">
        {children}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="ml-auto inline-flex items-center gap-1 text-[#c9d3da] hover:text-white"
      >
        <X className="size-4" aria-hidden />
        Снять выделение
      </button>
    </section>
  );
}

/** Итог группового действия: сколько изменено, что пропущено и почему. */
export function BulkNotice({ result, onClose }: { result: BulkResult; onClose: () => void }) {
  const [open, setOpen] = useState(false);
  const skipped = result.skipped.length;
  return (
    <div
      role="status"
      className={clsx(
        'mb-3 rounded-md border px-3 py-2 text-[13.5px]',
        skipped ? 'border-signal/50 bg-signal-soft' : 'border-ok/30 bg-ok-soft',
      )}
    >
      <div className="flex items-start gap-3">
        <p className="min-w-0 flex-1">
          Изменено: <b className="tabular-nums">{result.updated}</b>
          {skipped > 0 && (
            <>
              {' · '}пропущено {skipped} {plural(skipped, ['запись', 'записи', 'записей'])}{' '}
              <button type="button" className="link" onClick={() => setOpen((v) => !v)}>
                {open ? 'скрыть' : 'почему'}
              </button>
            </>
          )}
        </p>
        <button type="button" onClick={onClose} aria-label="Закрыть" className="text-steel hover:text-ink">
          <X className="size-4" />
        </button>
      </div>
      {open && (
        <ul className="mt-2 max-h-48 list-disc overflow-y-auto pl-5 text-ink-soft">
          {result.skipped.map((s) => (
            <li key={s.id}>
              {s.number ? `#${s.number} ${s.title}` : 'Запись недоступна'} — {s.reason}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------- Фильтр ----------

/** Готовые фильтры одной строкой: «В работе», «Просрочены»… Повторный клик снимает. */
export function PresetChips<K extends string>({
  value,
  options,
  onChange,
  label,
}: {
  value?: K;
  options: { value: K; label: string; count?: number; tone?: 'danger' | 'signal' }[];
  onChange: (v: K | undefined) => void;
  label: string;
}) {
  return (
    <fieldset className="flex min-w-0 flex-wrap items-center gap-1.5">
      <legend className="sr-only">{label}</legend>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            onClick={() => onChange(on ? undefined : o.value)}
            className={clsx(
              'inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[13px] whitespace-nowrap transition-colors',
              on
                ? 'border-graphite bg-graphite text-white'
                : 'border-line-strong bg-surface text-ink-soft hover:border-steel hover:text-ink',
            )}
          >
            {o.label}
            {o.count ? (
              <span
                className={clsx(
                  'min-w-[18px] rounded-full px-1 text-center text-[11.5px] font-semibold leading-[17px] tabular-nums',
                  on
                    ? 'bg-white/20 text-white'
                    : o.tone === 'danger'
                      ? 'bg-danger text-white'
                      : o.tone === 'signal'
                        ? 'bg-signal text-ink'
                        : 'bg-sunken text-ink-soft',
                )}
              >
                {o.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </fieldset>
  );
}

/** Кнопка «Фильтр» с числом заданных условий. */
export function FilterToggle({
  open,
  active,
  onToggle,
  controls,
}: {
  open: boolean;
  active: number;
  onToggle: () => void;
  controls: string;
}) {
  return (
    <Button
      variant="secondary"
      size="sm"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      className={clsx('h-9', active > 0 && 'border-graphite')}
    >
      <SlidersHorizontal className="size-4" aria-hidden />
      Фильтр
      {active > 0 && (
        <span className="min-w-[18px] rounded-full bg-graphite px-1 text-center text-[11.5px] font-semibold leading-[18px] text-white tabular-nums">
          {active}
        </span>
      )}
    </Button>
  );
}

/** Раскрывающаяся панель условий фильтра. */
export function FilterPanel({
  id,
  children,
  onReset,
}: {
  id: string;
  children: ReactNode;
  onReset: () => void;
}) {
  return (
    <section id={id} aria-label="Условия фильтра" className="panel mb-4 p-4">
      <div className="grid gap-x-4 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
      <div className="mt-3 flex justify-end">
        <button type="button" className="link text-[13.5px]" onClick={onReset}>
          Сбросить всё
        </button>
      </div>
    </section>
  );
}

/** Подпись + поле для панели фильтра. */
export function FilterField({ label, children }: { label: string; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="min-w-0">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      {children(id)}
    </div>
  );
}

/** Период «с — по» (даты без времени). */
export function DateRange({
  label,
  from,
  to,
  onChange,
}: {
  label: string;
  from?: string;
  to?: string;
  onChange: (from: string | undefined, to: string | undefined) => void;
}) {
  const id = useId();
  return (
    <fieldset className="min-w-0">
      <legend className="field-label">{label}</legend>
      <div className="flex items-center gap-1.5">
        <Input
          id={`${id}-from`}
          type="date"
          aria-label={`${label}: с`}
          value={from ?? ''}
          max={to}
          onChange={(e) => onChange(e.target.value || undefined, to)}
          className="min-w-0 px-2"
        />
        <span aria-hidden className="text-steel">
          —
        </span>
        <Input
          id={`${id}-to`}
          type="date"
          aria-label={`${label}: по`}
          value={to ?? ''}
          min={from}
          onChange={(e) => onChange(from, e.target.value || undefined)}
          className="min-w-0 px-2"
        />
      </div>
    </fieldset>
  );
}

// ---------- Сохранённые фильтры ----------

const savedFiltersQuery = (page: SavedFilterPage) => ({
  queryKey: ['saved-filters', page],
  queryFn: () => api<SavedFilterItem[]>('/saved-filters', { query: { page } }),
  staleTime: 60_000,
});

type Params = Record<string, string | number | boolean>;

/** Убираем пустые значения: так фильтры сравниваются и сохраняются одинаково. */
export function cleanParams(p: Record<string, unknown>): Params {
  const out: Params = {};
  for (const [k, v] of Object.entries(p)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') out[k] = v;
  }
  return out;
}

function sameParams(a: Params, b: Params) {
  const ka = Object.keys(a).sort();
  const kb = Object.keys(b).sort();
  return ka.length === kb.length && ka.every((k, i) => k === kb[i] && String(a[k]) === String(b[k]));
}

/**
 * «Мои фильтры»: список сохранённых, «Сохранить текущий…» и удаление. Видны только самому человеку.
 * current — параметры текущего фильтра (без вида и страницы).
 */
export function SavedFilters({
  page,
  current,
  onApply,
}: {
  page: SavedFilterPage;
  current: Params;
  onApply: (params: Params) => void;
}) {
  const qc = useQueryClient();
  const list = useQuery(savedFiltersQuery(page));
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const box = useRef<HTMLDivElement>(null);
  const menuId = useId();
  const activeFilter = list.data?.find((f) => sameParams(f.params, current));

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const save = useMutation({
    mutationFn: () =>
      api<SavedFilterItem>('/saved-filters', { method: 'POST', body: { page, name, params: current } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['saved-filters', page] });
      setSaving(false);
      setName('');
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/saved-filters/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['saved-filters', page] }),
  });

  return (
    <div ref={box} className="relative">
      <Button
        variant="secondary"
        size="sm"
        className={clsx('h-9 max-w-[220px]', activeFilter && 'border-graphite')}
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <Bookmark className={clsx('size-4 shrink-0', activeFilter && 'fill-signal text-ink')} aria-hidden />
        <span className="truncate">{activeFilter?.name ?? 'Мои фильтры'}</span>
        <ChevronDown className="size-3.5 shrink-0" aria-hidden />
      </Button>
      {open && (
        <div
          id={menuId}
          className="absolute right-0 z-30 mt-1 w-[280px] max-w-[calc(100vw-2rem)] rounded-[var(--radius-panel)] border border-line-strong bg-surface py-1 shadow-[0_8px_24px_-8px_rgb(24_34_43/0.35)]"
        >
          {list.data?.length ? (
            <ul className="max-h-72 overflow-y-auto">
              {list.data.map((f) => (
                <li key={f.id} className="group flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      onApply(f.params);
                      setOpen(false);
                    }}
                    className={clsx(
                      'min-w-0 flex-1 truncate px-3 py-2 text-left text-[13.5px] hover:bg-sunken',
                      f.id === activeFilter?.id && 'font-medium',
                    )}
                  >
                    {f.name}
                  </button>
                  <button
                    type="button"
                    aria-label={`Удалить фильтр «${f.name}»`}
                    onClick={() => remove.mutate(f.id)}
                    className="px-3 py-2 text-steel hover:text-danger"
                  >
                    <Trash2 className="size-3.5" aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
          ) : (
            <p className="px-3 py-2 text-[13px] text-steel">
              Сохранённых фильтров нет. Задайте условия и сохраните — они будут здесь.
            </p>
          )}
          <div className="border-t border-line px-1 pt-1">
            <button
              type="button"
              disabled={!Object.keys(current).length}
              onClick={() => {
                setOpen(false);
                setSaving(true);
              }}
              className="w-full rounded px-2 py-2 text-left text-[13.5px] font-medium hover:bg-sunken disabled:text-steel disabled:hover:bg-transparent"
            >
              Сохранить текущий фильтр…
            </button>
          </div>
        </div>
      )}
      {saving && (
        <Dialog title="Сохранить фильтр" onClose={() => setSaving(false)} width={420}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              save.mutate();
            }}
            className="flex flex-col gap-3"
          >
            <div>
              <label htmlFor={`${menuId}-name`} className="field-label">
                Название
              </label>
              <Input
                id={`${menuId}-name`}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Например: Сделки отдела в работе"
                maxLength={100}
              />
              <p className="mt-1 text-[12.5px] text-steel">
                Фильтр увидите только вы. То же название заменит старый фильтр.
              </p>
            </div>
            {save.error && <p className="text-[13px] text-danger">{save.error.message}</p>}
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setSaving(false)}>
                Отмена
              </Button>
              <Button type="submit" loading={save.isPending} disabled={!name.trim()}>
                Сохранить
              </Button>
            </div>
          </form>
        </Dialog>
      )}
    </div>
  );
}

// ---------- Excel ----------

/** Ссылка на выгрузку: тот же фильтр, что и на экране; файл скачивается браузером. */
export function ExportButton({ path, query }: { path: string; query: Record<string, unknown> }) {
  const url = new URL(`/api${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '' && v !== false) url.searchParams.set(k, String(v));
  }
  return (
    <a
      href={url.pathname + url.search}
      download
      className="inline-flex h-9 items-center gap-2 rounded-[var(--radius-control)] border border-line-strong bg-surface px-3 text-[13px] font-medium whitespace-nowrap hover:bg-sunken"
      title="Скачать список по текущему фильтру в Excel"
    >
      <Download className="size-4" aria-hidden />
      Excel
    </a>
  );
}

/** Сколько условий задано (для числа на кнопке «Фильтр»). */
export function countActive(values: unknown[]) {
  return values.filter((v) => v !== undefined && v !== null && v !== '' && v !== false).length;
}

export function pluralRecords(n: number) {
  return `${n} ${plural(n, ['запись', 'записи', 'записей'])}`;
}

// ---------- Выбор колонок ----------

export interface ColumnDef<K extends string = string> {
  key: K;
  label: string;
  /** Скрыта, пока человек сам не включит */
  defaultHidden?: boolean;
}

const userSettingsQuery = {
  queryKey: ['user-settings'],
  queryFn: () => api<Record<string, unknown>>('/user-settings'),
  staleTime: 10 * 60_000,
};

/** Какие колонки таблицы видны — настройка человека (на всех устройствах одинаково). */
export function useColumns<K extends string>(table: ColumnTable, columns: ColumnDef<K>[]) {
  const qc = useQueryClient();
  const settings = useQuery(userSettingsQuery);
  const key = columnsSettingKey(table);
  const saved = settings.data?.[key];
  const hidden = new Set<string>(
    Array.isArray(saved) ? (saved as string[]) : columns.filter((c) => c.defaultHidden).map((c) => c.key),
  );
  const save = useMutation({
    mutationFn: (value: string[]) => api(`/user-settings/${key}`, { method: 'PUT', body: { value } }),
    onMutate: (value) => {
      qc.setQueryData(['user-settings'], (old: Record<string, unknown> | undefined) => ({
        ...old,
        [key]: value,
      }));
    },
  });
  return {
    columns,
    show: (k: K) => !hidden.has(k),
    toggle: (k: K) => {
      const next = new Set(hidden);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      save.mutate([...next]);
    },
    reset: () => save.mutate(columns.filter((c) => c.defaultHidden).map((c) => c.key)),
    hiddenCount: columns.filter((c) => hidden.has(c.key)).length,
  };
}

export type ColumnsState<K extends string> = ReturnType<typeof useColumns<K>>;

/** Кнопка «Колонки» со списком галочек. */
export function ColumnsMenu<K extends string>({ state }: { state: ColumnsState<K> }) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const menuId = useId();
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={box} className="relative">
      <Button
        variant="secondary"
        size="sm"
        className="h-9"
        aria-expanded={open}
        aria-controls={menuId}
        onClick={() => setOpen((v) => !v)}
      >
        <Columns3 className="size-4" aria-hidden />
        Колонки
      </Button>
      {open && (
        <fieldset
          id={menuId}
          className="absolute right-0 z-30 mt-1 w-[230px] rounded-[var(--radius-panel)] border border-line-strong bg-surface py-1 shadow-[0_8px_24px_-8px_rgb(24_34_43/0.35)]"
        >
          <legend className="sr-only">Видимые колонки</legend>
          {state.columns.map((c) => (
            <label
              key={c.key}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-[13.5px] hover:bg-sunken"
            >
              <input
                type="checkbox"
                checked={state.show(c.key)}
                onChange={() => state.toggle(c.key)}
                className="size-4 accent-[var(--color-graphite)]"
              />
              {c.label}
            </label>
          ))}
          <div className="mt-1 border-t border-line px-3 pt-1.5 pb-1">
            <button type="button" className="link text-[13px]" onClick={state.reset}>
              Как было
            </button>
          </div>
        </fieldset>
      )}
    </div>
  );
}
